import React, { useCallback, useMemo } from "react";
import { getDate, getMonth, getYear, isValid, parseISO } from "date-fns";
import { isEmpty, isNil } from "ramda";

import { Field, FieldLabel, ValidationError } from "affinityUI/form/components";
import { DateInput, DateParts } from "affinityUI/form/inputs/dateInput";

import { useAffinityField } from "../../helpers";

const validateDate = (date?: Maybe<string>) => {
  if (!isNil(date) && !isValid(parseISO(date))) {
    return "Please enter correct date in format DD-MM-YYYY";
  }
};

const zeroPad = (num: number | string): string => (isEmpty(num) ? "" : String(num).padStart(2, "0"));

const getDateParts = (date?: string): DateParts => {
  if (!date) {
    return {
      day: "",
      month: "",
      year: "",
    };
  }
  const parsedDate = isValid(date) ? date : parseISO(date);

  if (isValid(parsedDate)) {
    return {
      day: zeroPad(getDate(parsedDate as Date)),
      month: zeroPad(getMonth(parsedDate as Date) + 1),
      year: String(getYear(parsedDate as Date)),
    };
  }
  const [year, month, day] = date.split("-");
  return { day: zeroPad(day), month: zeroPad(month), year };
};

export const DateField: React.FC<React.PropsWithChildren<{ name: string }>> = ({ children, name }) => {
  const [{ value, ...field }, { error }, { setValue, setError }] = useAffinityField({
    name,
    validate: validateDate,
  });
  const groupId = `field-${name}`;

  const handleChange = useCallback(
    (dateParts) => {
      setError(undefined);
      const valueToSet = `${dateParts.year}-${zeroPad(dateParts.month)}-${zeroPad(dateParts.day)}`;
      setValue(valueToSet === "--" ? "" : valueToSet);
    },
    [setError, setValue]
  );

  const dateParts = useMemo(() => getDateParts(value), [value]);

  return (
    <Field role="group" id={name} aria-labelledby={groupId}>
      <FieldLabel id={groupId}>{children}</FieldLabel>
      <DateInput error={error} {...field} value={dateParts} onChange={handleChange} />
      {error && <ValidationError>{error}</ValidationError>}
    </Field>
  );
};
